import { faqsPl } from "@data/saas-landing/faq";
import {
  aboutUsCopyPl,
  contactFormPl,
  faqStructuredData,
  mobileClusterLinks,
  mobileKnowledgeBaseLinks,
  organizationStructuredData,
  serviceStructuredData,
} from "@data/mobile-web-landing/shared";
import {
  Building2,
  Calendar,
  CheckCircle,
  Code,
  CreditCard,
  FileText,
  LayoutDashboard,
  LifeBuoy,
  PenTool,
  Rocket,
  Search,
  ShieldCheck,
  Sparkles,
  Users,
} from "lucide-react";

import type { FAQCopy } from "@components/bluetooth-landing/FAQ";
import type { FooterCopy } from "@components/bluetooth-landing/Footer";
import type { HeroCopy } from "@components/bluetooth-landing/Hero";
import type { ProcessCopy } from "@components/bluetooth-landing/Process";
import type { ServicesCopy } from "@components/bluetooth-landing/Services";
import type { TargetAudienceCopy } from "@components/bluetooth-landing/TargetAudience";
import type { ValuePropositionCopy } from "@components/bluetooth-landing/ValueProposition";

export const title =
  "SaaS na zamówienie - projektowanie i rozwój aplikacji webowych";

export const description =
  "SaaS na zamówienie: MVP platformy SaaS, subskrypcje, multi-tenant i panel admina. Polski software house — gdy gotowy SaaS nie pasuje do Twojego procesu.";

export const structuredData = [
  organizationStructuredData(description),
  serviceStructuredData(
    "SaaS na zamówienie",
    description,
    "Custom SaaS application development",
  ),
  faqStructuredData(faqsPl),
];

export const heroCopy: HeroCopy = {
  header: "SaaS",
  headerTail: "na zamówienie",
  description:
    "Projektujemy i rozwijamy dedykowane platformy SaaS — rejestracja, role, subskrypcje i panel admina. Dla firm, którym gotowy produkt „nie pasuje” i potrzebują własnej aplikacji webowej.",
  cta: {
    text: "Darmowa konsultacja",
    href: "#kontakt",
  },
  secondaryCta: {
    text: "Poznaj nasz proces",
    href: "#proces",
  },
  benefits: [
    { text: "MVP SaaS z panelem", Icon: CheckCircle },
    { text: "Stripe / płatności EU", Icon: CheckCircle },
    { text: "Multi-tenant i RODO", Icon: CheckCircle },
  ],
};

export const targetAudienceCopy: TargetAudienceCopy = {
  header: {
    h2: "Kim są nasi klienci?",
    h3: "Dla kogo jest SaaS na zamówienie?",
    p: "SaaS na zamówienie ma sens przy MVP dla twórców i MŚP, platformach z subskrypcją oraz gdy gotowe oprogramowanie nie pokrywa procesu biznesowego.",
  },
  points: [
    {
      Icon: Building2,
      title: "Gotowy SaaS nie wystarcza — potrzebujesz dedykowanej aplikacji",
      description:
        "Proces, role albo integracje wykraczają poza off-the-shelf. Budujesz własną platformę zamiast dokładać kolejne obejścia.",
    },
    {
      Icon: Rocket,
      title: "Startujesz MVP platformy SaaS (React, Node, PostgreSQL)",
      description:
        "Chcesz wersję MVP z rejestracją, rolami i podstawową subskrypcją — szybko zweryfikować product-market fit.",
    },
    {
      Icon: Users,
      title: "Budujesz produkt dla wielu klientów (multi-tenant)",
      description:
        "Panel twórcy, klienta i admina — izolacja danych i uprawnienia od pierwszego dnia albo ewolucyjnie.",
    },
    {
      Icon: CreditCard,
      title: "Potrzebujesz subskrypcji i płatności (Stripe, BLIK…)",
      description:
        "Monetyzacja jest częścią MVP, nie „fazą 2”. Projektujemy billing razem z produktem.",
    },
    {
      Icon: Sparkles,
      title: "Chcesz SaaS z funkcjami AI",
      description:
        "AI jako cecha platformy (chatbot, automatyzacja) — łączymy z osobną ofertą integracji AI, bez osobnego „projektu AI”.",
    },
  ],
  cta: {
    h4: "Brzmi znajomo?",
    text: "Opisz wizję SaaS i etap (pomysł, MVP, skalowanie) — zaproponujemy zakres i orientacyjną wycenę.",
    href: "#kontakt",
    buttonText: "Umów darmową konsultację",
  },
};

export const valuePropositionCopy: ValuePropositionCopy = {
  header: {
    h2: "Nasza specjalizacja",
    h3: "Dlaczego warto budować SaaS z nami?",
    p: "Dedykowany SaaS to produkt biznesowy: konta, uprawnienia, płatności i skalowanie. Projektujemy pod realnych użytkowników i koszty utrzymania — nie pod slajd inwestorski.",
    imageAlt: "SaaS na zamówienie — platforma webowa",
  },
  values: [
    {
      Icon: Rocket,
      title: "MVP z mierzalnym zakresem",
      description:
        "Rejestracja, role, kluczowy workflow i panel — wystarczy, by walidować rynek bez budowania „wszystkiego naraz”.",
    },
    {
      Icon: CreditCard,
      title: "Subskrypcje i płatności w UE",
      description:
        "Stripe, Przelewy24, BLIK — billing zaprojektowany pod Wasz model (miesięczny, usage, freemium).",
    },
    {
      Icon: LayoutDashboard,
      title: "Panele dla twórcy, klienta i admina",
      description:
        "Osobne perspektywy użytkowników (twórca, klient, admin) budujemy świadomie — bo od tego zależy codzienna praca na platformie.",
    },
    {
      Icon: ShieldCheck,
      title: "RODO i dane w UE",
      description:
        "Hosting, powierzenie danych i retencja — szczególnie przy SaaS dla wielu organizacji.",
    },
    {
      Icon: LifeBuoy,
      title: "Od MVP do produkcji",
      description:
        "Hardening, monitoring, uprawnienia i utrzymanie po premierze — nie zostawiamy „demo na produkcji”.",
    },
  ],
};

export const servicesCopy: ServicesCopy = {
  header: {
    h2: "Usługi",
    h3: "SaaS na zamówienie — co budujemy",
    p: "Od warsztatów produktowych po platformę z płatnościami i multi-tenant. Stack dobieramy do zespołu i budżetu startupu lub MŚP.",
  },
  services: [
    {
      Icon: Search,
      title: "Discovery i zakres MVP SaaS",
      description:
        "Warsztaty, user stories, model monetyzacji i priorytety — plan MVP z widełkami kosztów.",
    },
    {
      Icon: Code,
      title: "Aplikacja webowa SaaS (np. React + Node + Postgres)",
      description:
        "Frontend, API, baza i wdrożenie — sprawdzony stack na pierwsze MVP platformy SaaS.",
    },
    {
      Icon: CreditCard,
      title: "Subskrypcje i billing",
      description:
        "Integracja płatności, plany, faktury i obsługa statusów subskrypcji w panelu.",
    },
    {
      Icon: Users,
      title: "Multi-tenant i role",
      description:
        "Izolacja organizacji, zaproszenia użytkowników, uprawnienia i audyt działań admina.",
    },
    {
      Icon: Sparkles,
      title: "Funkcje AI w SaaS (opcjonalnie)",
      description:
        "Chatbot, rekomendacje lub automatyzacja jako moduł produktu — z kontrolą kosztów API.",
    },
    {
      Icon: LifeBuoy,
      title: "Wdrożenie, SLA i rozwój produktu",
      description:
        "Produkcja, monitoring i kolejne iteracje po kontakcie z użytkownikami.",
    },
  ],
  cta: {
    h4: "Nie wiesz, czy SaaS na zamówienie to dobry kierunek?",
    p: "Porozmawiajmy o gotowym produkcie vs dedykowanej platformie — pomożemy wybrać sensowny zakres MVP.",
    button: {
      text: "Umów darmową konsultację",
      href: "#kontakt",
    },
  },
};

export const processCopy: ProcessCopy = {
  header: {
    h2: "Nasz Proces",
    h3: "Jak powstaje SaaS na zamówienie",
    p: "Od warsztatów i wyceny MVP po billing, hardening i utrzymanie. Etapy dopasowane do founderów i operatorów MŚP.",
  },
  stepNumber: "Krok",
  steps: [
    {
      Icon: Calendar,
      title: "Darmowe spotkanie online",
      duration: "w kilka godzin",
      description:
        "Omawiamy pomysł SaaS, klientów docelowych, budżet i czy startujemy od wąskiego MVP.",
    },
    {
      Icon: FileText,
      title: "Warsztaty produktowe",
      duration: "w kilka dni",
      description:
        "Mapujemy role, workflow, płatności i integracje. Ustalamy, co wchodzi do MVP, a co później.",
    },
    {
      Icon: PenTool,
      title: "Wycena i umowa",
      duration: "ustalenie harmonogramu",
      description:
        "Fixed price na jasny zakres MVP albo T&M na rozwój produktu — z kamieniami milowymi.",
    },
    {
      Icon: Code,
      title: "Budowa MVP platformy",
      duration: "min. 8–16 tygodni",
      description:
        "Aplikacja webowa, API, panel i kluczowe ścieżki użytkownika gotowe do testów.",
    },
    {
      Icon: CreditCard,
      title: "Płatności i przygotowanie produkcji",
      duration: "2–4 tygodnie",
      description:
        "Billing, uprawnienia, monitoring i hardening przed udostępnieniem klientom.",
    },
    {
      Icon: CheckCircle,
      title: "Uruchomienie i rozwój",
      duration: "na stałe",
      description:
        "Wsparcie po starcie, kolejne funkcje i optymalizacja na podstawie użycia.",
    },
  ],
  cta: {
    href: "#kontakt",
    text: "Wyceń SaaS na zamówienie",
  },
};

export const aboutUsCopy = aboutUsCopyPl({
  h3: "Polski zespół platform SaaS i aplikacji webowych",
  imageAlt: "Zespół Blues Brackets — SaaS na zamówienie",
});

export const faqCopy: FAQCopy = {
  header: {
    h2: "FAQ",
    h3: "Najczęściej zadawane pytania o SaaS na zamówienie",
  },
  faqs: faqsPl,
};

export const footerCopy: FooterCopy = {
  header: {
    h2: "Budujesz platformę SaaS?",
    p: "Umów się na darmową konsultację — opowiedz o MVP, subskrypcjach i panelu, a zaproponujemy zakres współpracy.",
  },
  cta: {
    href: "#kontakt",
    text: "Umów darmową konsultację",
    p: "SaaS na zamówienie — dedykowana aplikacja webowa, billing i multi-tenant. Polski software house.",
    email: "hello@bluesbrackets.com",
    phone: "+48 535 462 678",
  },
  links: {
    h4: "Na skróty",
    links: [
      { href: "#uslugi", text: "Nasze usługi" },
      { href: "#proces", text: "Proces współpracy" },
      { href: "#faq", text: "FAQ" },
      {
        href: "/aplikacje-mobilne-i-webowe",
        text: "Aplikacje mobilne i webowe",
      },
      ...mobileClusterLinks.filter(
        l =>
          l.href !== "/aplikacje-mobilne-i-webowe" &&
          l.href !== "/saas-na-zamowienie",
      ),
    ],
  },
  knowledgeBase: {
    h4: "Baza wiedzy",
    links: [...mobileKnowledgeBaseLinks],
  },
  copyright: "bluesBrackets. Wszelkie prawa zastrzeżone.",
  privacyPolicy: "Polityka prywatności",
  privacyPolicyHref: "/policy",
  contactForm: contactFormPl,
};
